// querySources — the relations a statement actually reads, as the VQE column
// pickers and emitters need them: what the query calls each one (display),
// the SQL text that qualifies its columns, and — for base tables only — the
// schema-snapshot key its columns live under.
//
// Why not `scope.reachableTables`: the resolver unions the query's bindings
// with EVERY schema table (right for IntelliSense, wrong for "which table does
// this column come from"), carries no binding for an unaliased FROM table
// other than the snapshot's own entry (so its catalog spelling leaked into the
// SQL as the qualifier), and treats a CTE / derived-table name like a table
// key — a CTE named `orders` then borrowed the base table's columns.
//
// A base table is looked up by the snapshot's own fold of its NAME, whatever
// the query's quoting: every projector folds a catalog name with
// `foldFor(engine)`, so `"VqeMiMixed"` (PostgreSQL) and `[Order Details]` (SQL
// Server) must be looked up folded too, or their columns never match. With a
// snapshot, the reference is then resolved to the snapshot RELATION it names
// (`lookupSnapshotRelation`: schema included — `FROM orders` is the default
// schema's `orders`, never another schema's same-named table; an ambiguous
// bare name resolves to nothing), and that relation is the source's identity.

import type { QualifiedName, SqlEngine } from '../../sql-grammar/model';
import { foldFor } from '../service/schemaProjectionHelpers';
import {
  lookupRelationColumns,
  lookupSnapshotRelation,
  sameRelation,
  type RelationIdentity,
} from '../service/snapshotRelations';
import { aliasSql, boundLeafSql } from '../suggest/sqlIdentifierText';
import type { FromItem, QueryStructure } from '../types/queryStructure';
import type { Column, SchemaSnapshot } from '../types/schema';

export type QuerySourceKind = 'relation' | 'cte' | 'derived' | 'function';

export type QuerySource = {
  readonly kind: QuerySourceKind;
  /** What the query calls it — the alias as typed, else the table / CTE name. Display only. */
  readonly exposedName: string;
  /** SQL that qualifies its columns: the alias, else the leaf name token as the query spells it. */
  readonly qualifierSql: string;
  /**
   * The name the query binds it under, keyed the way the engine compares
   * names (`bindingKeyOf`; the alias, else the table / CTE / function name).
   * An inner source with the same key hides an enclosing query's. Absent for
   * an unaliased derived table.
   */
  readonly bindingKey?: string;
  /**
   * Snapshot fold of a base table's NAME (`kind === 'relation'`, the leaf —
   * `relation` carries the schema-aware identity); absent for CTEs / derived
   * tables / functions.
   */
  readonly relationKey?: string;
  /** Snapshot fold of the schema the query qualified the table with, when it did. */
  readonly schemaKey?: string;
  /** The database the query qualified the table with, when it did (another database's table never resolves). */
  readonly databaseKey?: string;
  /**
   * The snapshot relation a base table resolves to (schema included), once
   * resolved against a snapshot (`resolveSourceRelation`); `null` when the
   * snapshot holds no such relation or the bare name is ambiguous — its
   * columns are then not known. Absent when no snapshot was consulted: the
   * reference itself (`relationKey` / `schemaKey`) is then the identity.
   */
  readonly relation?: QualifiedName | null;
  /** Snapshot fold of a CTE's name — a base table of that name is shadowed for unqualified references. */
  readonly shadowKey?: string;
  /** From an enclosing query (a correlated reference), not the statement being edited. */
  readonly outer?: boolean;
};

export type QuerySourceOptions = {
  /** Include a DML statement's own target (and a MERGE's source) — never in `fromItems`. */
  readonly withDmlTarget?: boolean;
  /** Resolve each base table to the snapshot relation it names (`relation`). */
  readonly snapshot?: SchemaSnapshot | null;
};

export function querySources(
  structure: QueryStructure | null | undefined,
  engine: SqlEngine,
  options: QuerySourceOptions = {},
): QuerySource[] {
  if (!structure) return [];
  const targets = options.withDmlTarget
    ? [structure.updateTarget, structure.deleteTarget, structure.mergeTarget, structure.mergeSource]
    : [];
  const items: FromItem[] = [
    ...targets.filter((item): item is FromItem => item != null),
    ...(structure.fromItems ?? []),
    ...(structure.joins ?? []).map((j) => j.right),
  ];
  return items.map((item) => resolveSourceRelation(querySourceOf(item, engine), options.snapshot));
}

/**
 * A base-table source resolved against the snapshot: `relation` is the
 * snapshot relation its reference names (`lookupSnapshotRelation` on the
 * snapshot-folded name / schema), or `null`. Other sources, and every source
 * when there is no snapshot, are returned as they are.
 */
export function resolveSourceRelation(
  source: QuerySource,
  snapshot: SchemaSnapshot | null | undefined,
): QuerySource {
  if (!snapshot || source.kind !== 'relation' || source.relationKey === undefined) return source;
  if (source.relation !== undefined) return source;
  const hit = lookupSnapshotRelation(snapshot, referenceIdentity(source));
  return { ...source, relation: hit?.qualified ?? null };
}

/** A base-table source's snapshot columns, resolved schema-aware; empty when not known. */
export function sourceColumns(source: QuerySource, snapshot: SchemaSnapshot | null | undefined): readonly Column[] {
  const relation = resolveSourceRelation(source, snapshot).relation;
  return relation ? (lookupRelationColumns(snapshot, relation) ?? []) : [];
}

/**
 * The relation identity a base-table source stands for: its resolved snapshot
 * relation, else (no snapshot consulted) the reference under the snapshot's
 * fold. `undefined` for a CTE / derived / function source and for a reference
 * the snapshot could not resolve.
 */
export function sourceRelation(source: QuerySource): RelationIdentity | undefined {
  if (source.kind !== 'relation' || source.relationKey === undefined || source.relation === null) return undefined;
  return source.relation ?? referenceIdentity(source);
}

/**
 * The base-table source standing for `relation` (a column's relation, an FK
 * edge's table): a source resolved to exactly that relation first, then one
 * whose identity matches by name (a schema on both sides must agree).
 */
export function findRelationSource(
  sources: readonly QuerySource[],
  relation: RelationIdentity,
): QuerySource | undefined {
  const resolved = sources.find((s) => s.relation && sameRelation(s.relation, relation));
  if (resolved) return resolved;
  return sources.find((s) => {
    const identity = sourceRelation(s);
    return identity !== undefined && sameRelation(identity, relation);
  });
}

function referenceIdentity(source: QuerySource): RelationIdentity {
  return {
    foldedName: source.relationKey ?? '',
    ...(source.schemaKey ? { foldedSchema: source.schemaKey } : {}),
    ...(source.databaseKey ? { foldedDatabase: source.databaseKey } : {}),
  };
}

/**
 * The key a bound name (`folded` — the resolver's quote-aware fold) compares
 * by on `engine`. PostgreSQL and Oracle compare the fold itself: a quoted
 * name exactly, an unquoted one folded (`"O"` ≠ `o` on PostgreSQL, `"O"` =
 * `o` on Oracle). SQL Server (default collation) and SQLite compare
 * identifiers case-insensitively, quoted or not (`[O]` = `o`). MySQL table
 * aliases are case-sensitive on a server with `lower_case_table_names = 0`,
 * which the app does not read: they compare case-insensitively here, so an
 * inner `O` hides an outer `o` — at worst an outer table is not offered, never
 * one whose columns an inner name would read.
 */
export function bindingKeyOf(engine: SqlEngine, folded: string): string {
  return engine === 'postgres' || engine === 'oracle' ? folded : folded.toLowerCase();
}

export function querySourceOf(item: FromItem, engine: SqlEngine): QuerySource {
  const fold = foldFor(engine);
  const alias = item.alias;
  const aliasText = alias ? aliasSql(alias.alias, alias.aliasFolded, engine) : undefined;
  const aliasKey = alias ? { bindingKey: bindingKeyOf(engine, alias.aliasFolded) } : undefined;
  const t = item.target;
  if (t.kind === 'relation') {
    return {
      kind: 'relation',
      exposedName: alias?.alias ?? t.qualified.name,
      qualifierSql: aliasText ?? boundLeafSql(t.qualified, engine),
      ...(aliasKey ?? { bindingKey: bindingKeyOf(engine, t.qualified.foldedName) }),
      relationKey: fold(t.qualified.name),
      ...(t.qualified.schema ? { schemaKey: fold(t.qualified.schema) } : {}),
      ...(t.qualified.database ? { databaseKey: fold(t.qualified.database) } : {}),
    };
  }
  if (t.kind === 'cte-ref') {
    return {
      kind: 'cte',
      exposedName: alias?.alias ?? t.cte.name.name,
      qualifierSql: aliasText ?? boundLeafSql(t.cte.name, engine),
      ...(aliasKey ?? { bindingKey: bindingKeyOf(engine, t.cte.name.foldedName) }),
      shadowKey: fold(t.cte.name.name),
    };
  }
  if (t.kind === 'function-call') {
    return {
      kind: 'function',
      exposedName: alias?.alias ?? t.routine.name,
      qualifierSql: aliasText ?? boundLeafSql(t.routine, engine),
      ...(aliasKey ?? { bindingKey: bindingKeyOf(engine, t.routine.foldedName) }),
    };
  }
  return { kind: 'derived', exposedName: alias?.alias ?? '(subquery)', qualifierSql: aliasText ?? '', ...aliasKey };
}

/** Snapshot folds of every CTE the statement declares — they shadow same-named base tables. */
export function declaredCteKeys(structure: QueryStructure | null | undefined, engine: SqlEngine): Set<string> {
  const fold = foldFor(engine);
  return new Set((structure?.ctes ?? []).map((c) => fold(c.name.name)));
}
